import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  FormControlLabel,
  Checkbox,
  Typography,
  Autocomplete,
} from "@mui/material";
import { SessionData } from "../../hooks/useSession";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useApiError } from "../../hooks/useApiError";
import DialogContentText from "@mui/material/DialogContentText";

interface SessionFormModalProps {
  open: boolean;
  handleClose: () => void;
  initialData?: SessionData & { hasDependencies?: boolean };
  onSubmit: (formData: SessionData) => void;
  isEdit: boolean;
  schoolId: number;
}

const SessionFormModal: React.FC<SessionFormModalProps> = ({
  open,
  handleClose,
  initialData,
  onSubmit,
  isEdit,
  schoolId,
}) => {
  const { t } = useTranslation();
  const { handleApiError } = useApiError();

  // Generate session options (last 20 + current + next 20)
  const generateSessionOptions = () => {
    const currentYear = new Date().getFullYear();
    const options = [];

    // Generate last 20 sessions
    for (let i = 20; i >= 1; i--) {
      const year = currentYear - i;
      options.push(`${year}-${year + 1}`);
    }

    // Add current session
    const currentSession = `${currentYear}-${currentYear + 1}`;
    options.push(currentSession);

    // Generate next 20 sessions
    for (let i = 1; i <= 20; i++) {
      const year = currentYear + i;
      options.push(`${year}-${year + 1}`);
    }

    return options;
  };

  const sessionOptions = generateSessionOptions();
  const currentSession = `${new Date().getFullYear()}-${
    new Date().getFullYear() + 1
  }`;

  const initialFormState: SessionData = {
    id: undefined,
    name: "", // No default session selected
    startDate: "",
    endDate: "",
    isActive: false,
    schoolId: schoolId,
  };

  const [formData, setFormData] = useState<SessionData>(initialFormState);
  const [selectedSession, setSelectedSession] =
    useState<string | null>(null); // No default selection
  const [hasDependencies, setHasDependencies] = useState<boolean>(false);

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        const { hasDependencies: deps, ...sessionData } = initialData;
        setHasDependencies(deps || false);
        setFormData({ ...sessionData, startDate: "", endDate: "", schoolId });
        setSelectedSession(initialData.name);
      } else {
        setHasDependencies(false);
        setFormData({ ...initialFormState, schoolId });
        setSelectedSession(null); // No default selection
      }
    }
  }, [open, initialData, isEdit, schoolId, currentSession]);

  const handleSessionChange = (event: any, newValue: string | null) => {
    if (newValue) {
      setSelectedSession(newValue);
      setFormData((prev) => ({
        ...prev,
        name: newValue,
        isActive: newValue === currentSession, // Auto-set active if it's current session
      }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await onSubmit({ ...formData, startDate: "", endDate: "", schoolId });
      if (!isEdit) {
        setFormData({ ...initialFormState, schoolId });
        setSelectedSession(currentSession);
      }
    } catch (error) {
      let message = t("failed_to_submit_session");
      const err = error as any;
      // Try to extract the backend message
      if (err?.response?.data?.message) {
        message = err.response.data.message;
      } else if (err?.message) {
        message = err.message;
      }
      // setErrorDialogMessage(message);
      // setErrorDialogOpen(true);
    }
  };

  const isFormValid = formData.name && formData.name.trim() !== "";

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {isEdit ? t("edit_session") : t("add_new_session")}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Autocomplete
              options={sessionOptions}
              value={selectedSession ?? undefined}
              onChange={handleSessionChange}
              disabled={isEdit && hasDependencies}
              disableClearable
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("session_name")}
                  required
                  margin="dense"
                  size="small"
                  variant="outlined"
                  placeholder={t("select_or_type_session_name")}
                  inputProps={{ ...params.inputProps, readOnly: true }}
                  InputLabelProps={{
                    sx: {
                      "& .MuiFormLabel-asterisk": {
                        color: "red",
                      },
                    },
                  }}
                  sx={{
                    backgroundColor: "#f8f9fb",
                  }}
                />
              )}
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              sx={{
                backgroundColor: "#f8f9fb",
              }}
            />
            {isEdit && hasDependencies && (
              <Typography
                variant="body2"
                color="warning.main"
                sx={{ ml: 1, fontStyle: "italic" }}
              >
                {t("session_name_cannot_be_edited_after_creation")}
              </Typography>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                  sx={{ p: 0.5 }}
                />
              }
              label={<span>{t("active_session")}</span>}
              sx={{ ml: 1 }}
            />
            {formData.name === currentSession && (
              <Typography
                variant="body2"
                color="primary"
                sx={{ ml: 1, fontStyle: "italic" }}
              >
                {t("this_is_current_academic_session")}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end", pb: 2, mr: 4 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={!isFormValid}
            sx={{
              minWidth: 100,
              backgroundColor: "#3092e3",
              "&:hover": {
                backgroundColor: "primary.main",
              },
              "&.Mui-disabled": {
                backgroundColor: "#b2ebff",
                opacity: 0.6,
              },
            }}
          >
            {isEdit ? t("update") : t("create")}
          </Button>
          <Button
            type="button"
            onClick={handleClose}
            variant="outlined"
            sx={{
              minWidth: 100,
              borderColor: "#f44336", // red
              color: "#f44336",
              ml: 2,
              "&:hover": {
                borderColor: "#b71c1c",
                color: "#b71c1c",
                backgroundColor: "rgba(244,67,54,0.08)",
              },
            }}
          >
            {t("cancel")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SessionFormModal;
